import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Mail, ArrowLeft, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { API } from "../App";
import Footer from "../components/Footer";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (response.ok) {
        setSent(true);
      } else {
        let errorMessage = 'Something went wrong. Please try again.';
        try {
          const error = await response.json();
          errorMessage = error.detail || errorMessage;
        } catch (e) {
          // JSON parsing failed, use default message
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      toast.error(error.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
        <div className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-md animate-fade-in">
            <Link to="/" className="block text-center mb-12">
              <h1 
                className="text-3xl font-light text-[#2D2A26]"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                the soft ritual
              </h1>
            </Link>

            <div className="paper-card rounded-3xl p-8 md:p-10 text-center">
              <div className="w-16 h-16 rounded-full bg-[#7A9E7E]/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-[#7A9E7E]" />
              </div>
              <h2 
                className="text-2xl font-normal text-[#2D2A26] mb-4"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Check your email
              </h2>
              <p className="text-[#6E6A66] font-light mb-6">
                If an account exists for <strong>{email}</strong>, we've sent a password reset link. 
                The link will expire in 1 hour.
              </p>
              <p className="text-[#A8A4A0] text-sm mb-6">
                Don't see it? Check your spam folder.
              </p>
              <Link to="/login">
                <Button 
                  variant="outline"
                  className="rounded-full px-6 border-[#E6E2DD] text-[#6E6A66] hover:bg-[#E6E2DD]"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to sign in
                </Button>
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md animate-fade-in">
          <Link to="/" className="block text-center mb-12">
            <h1 
              className="text-3xl font-light text-[#2D2A26]"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              the soft ritual
            </h1>
          </Link>

          <div className="paper-card rounded-3xl p-8 md:p-10">
            <div className="w-14 h-14 rounded-full bg-[#A87C6D]/10 flex items-center justify-center mx-auto mb-6">
              <Mail className="w-7 h-7 text-[#A87C6D]" />
            </div>
            <h2 
              className="text-2xl font-normal text-[#2D2A26] text-center mb-2"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Reset your password
            </h2>
            <p className="text-[#6E6A66] text-center font-light mb-8">
              Enter your email and we'll send you a link to reset your password.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#6E6A66] font-light">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="rounded-xl py-6 border-[#E6E2DD] focus:border-[#A87C6D] focus:ring-[#A87C6D] bg-white"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full rounded-full py-6 bg-[#A87C6D] hover:bg-[#956d5f] text-white"
              >
                {loading ? "Sending..." : "Send reset link"}
              </Button>
            </form>

            <p className="text-center text-[#6E6A66] font-light mt-6">
              Remember your password?{" "}
              <Link to="/login" className="text-[#A87C6D] hover:underline">
                Sign in
              </Link>
            </p>
          </div>

          <p className="text-center mt-8">
            <Link to="/" className="text-[#A8A4A0] hover:text-[#6E6A66] text-sm">
              ← Back to home
            </Link>
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
